// Shared UI for the research-wizard API-key guard: the "Verifying API keys…" banner shown while the
// probe is in flight, and the inline red note shown under a Data Source toggle whose key isn't valid.
// Language models, search providers, tearsheet, and Parallel.ai keys are not shown to organizations or members.
import { Link } from 'react-router-dom';
import { paths } from '@/routes/paths';
import { ORG_ONLY_KEYS } from '@/features/apiKeys/catalog';
import { useAuthStore } from '@/store/authStore';
import type { OrgKeyName } from '@/types';

export function ApiKeyVerifyingBanner() {
  return (
    <div className="alert alert-info d-flex align-items-center mb-3" role="status">
      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
      <span>Verifying API keys…</span>
    </div>
  );
}

export function configureApiKeyLink(keyName?: OrgKeyName): { to: string; label: string } | null {
  const user = useAuthStore.getState().currentUser;
  const orgManaged = !keyName || ORG_ONLY_KEYS.has(keyName);
  if (orgManaged) {
    if (user?.isAdmin) {
      return { to: paths.admin.adminOps, label: 'Open Admin Ops' };
    }
    return null;
  }
  return { to: paths.settings.apiKeys, label: 'Configure API Key' };
}

export function ApiKeyDisabledNote({ reason, keyName }: { reason: string; keyName?: OrgKeyName }) {
  if (!reason) return null;
  const link = configureApiKeyLink(keyName);
  return (
    <div className="text-danger small mb-2">
      <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
      {reason}
      {link ? (
        <>
          {' '}
          <Link to={link.to}>{link.label}</Link>
        </>
      ) : null}
    </div>
  );
}
